import React, { FunctionComponent } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { useMarketplace } from 'hooks';
import styles from './EvaluationNotification.module.scss';
import { EvaluationReport } from '@spike/report-model';

export interface EvaluationNotificationProps {
    onAddEvaluation?: () => void;
    onViewResults?: (evaluationReport: EvaluationReport) => void;
    className?: string;
    isEvaluation?: boolean;
    hideNotification?: boolean;
    evaluationReport?: EvaluationReport;
    readOnly?: boolean;
}

export const EvaluationNotification: FunctionComponent<EvaluationNotificationProps> = ({
    onAddEvaluation,
    onViewResults,
    className,
    isEvaluation,
    evaluationReport,
    hideNotification,
    readOnly
}) => {
    const marketplace = useMarketplace();
    const isReadOnly = readOnly ?? false;

    if (!marketplace?.evaluationEnabled || isEvaluation || hideNotification) {
        return null;
    }

    const evaluationType = !evaluationReport ? 'Missing' : evaluationReport.expired ? 'Expired' : null;

    const failedEvaluations = [
        {
            condition: evaluationReport?.finalResultDaycare === 'fail',
            message: 'Failed Daycare Evaluation'
        },
        {
            condition: evaluationReport?.finalResultBoarding === 'fail',
            message: 'Failed Boarding Evaluation'
        },
        {
            condition: evaluationReport?.finalResultGrooming === 'fail',
            message: 'Failed Grooming Evaluation'
        }
    ].filter(notification => notification.condition);

    const handleAddEvaluation = () => {
        if (isReadOnly) return;
        onAddEvaluation?.();
    };

    const handleViewResults = () => {
        onViewResults?.(evaluationReport!);
    };

    return (
        <>
            {evaluationType && (
                <div
                    className={clsx(styles.alertContainer, { [styles.alertContainerDisabled]: isReadOnly })}
                    onClick={!isReadOnly ? handleAddEvaluation : undefined}
                >
                    <AlertNewBooking
                        className={styles.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EF4F57"
                        bgColor="#FBB9BC33"
                        bdColor="#EF4F57"
                    >
                        <span className={styles.alertText}>
                            {evaluationType} Evaluation.{' '}
                            <a
                                className={clsx(styles.updateLink, {
                                    [styles.updateLinkDisabled]: isReadOnly
                                })}
                            >
                                Add Evaluation
                            </a>
                        </span>
                    </AlertNewBooking>
                </div>
            )}

            {failedEvaluations.length > 0 && (
                <div className={styles.failedContainer}>
                    <div className={styles.failedBox}>
                        {failedEvaluations.map((failedEvaluation, index) => (
                            <div
                                key={index}
                                className={styles.failedItem}
                            >
                                <FontAwesomeIcon
                                    icon={faCircleXmark}
                                    className={styles.failedIcon}
                                />
                                <span className={styles.failedText}>{failedEvaluation.message}</span>
                            </div>
                        ))}
                        <div className={styles.failedActions}>
                            <button
                                className={styles.addButton}
                                onClick={handleAddEvaluation}
                                disabled={isReadOnly}
                            >
                                Add an Evaluation
                            </button>
                            <span
                                className={clsx(styles.viewResults, { [styles.viewResultsDisabled]: isReadOnly })}
                                onClick={handleViewResults}
                            >
                                <a
                                    className={clsx(styles.updateLink, {
                                        [styles.updateLinkDisabled]: isReadOnly
                                    })}
                                >
                                    View Results
                                </a>
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EvaluationNotification;
